import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { getJson, postJson } from '../global/func';
import Icon from './Icon';

/**
 * Who else may use this box.
 *
 * Shown to the owner only -- the panel is inside the box tab, which a member
 * does not see, and the server refuses every write here from anyone else
 * regardless.
 *
 * Transfer lives at the bottom, behind a confirmation, and can only name someone
 * already on the list above it. That is the safety story rather than a warning
 * label: a typed address is one slip from handing a living room to a stranger,
 * and there is no way back. Sharing first is a step the owner has already taken
 * deliberately, with the person's name shown back to them.
 */
const DeviceShare = ({ User, device, onTransferred }) => {
  const [state, setState] = useState(null);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  // Who the owner has said they want to hand the box to, held until they
  // confirm. The second press is the one that does it.
  const [handingTo, setHandingTo] = useState(null);

  const auth = {
    USER_ID: User.id,
    AUTH_KEY: User.auth_key,
    DEVICE_KEY: device?.device_key,
  };
  const shareUrl = encodeURI(import.meta.env.REACT_APP_API_SERVER_URL + '/device/share/');

  const load = useCallback(async () => {
    if (!device?.device_key) return;
    const result = await getJson(shareUrl, {
      USER_ID: User.id, AUTH_KEY: User.auth_key, DEVICE_KEY: device.device_key,
    });
    // A failure has to land somewhere other than "still loading". The server
    // answers 503 until the sharing migration has been applied, and without
    // this the box tab would sit under a spinner that never resolves -- which
    // looks far more broken than the feature simply not being ready.
    setState(result?.ok
      ? result.data
      : { unavailable: true, reason: result?.data?.error || '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device?.device_key, User.id, User.auth_key]);

  useEffect(() => { load(); }, [load]);

  const say = (result, fallback) => {
    if (result?.ok && !result.data?.error) {
      setNotice(result.data?.message || fallback);
      setError('');
      return true;
    }
    setError(result?.data?.error || 'That did not work. Try again in a moment.');
    setNotice('');
    return false;
  };

  const share = async (e) => {
    e.preventDefault();
    const address = email.trim();
    if (!address) return;

    setBusy(true);
    const result = await postJson(shareUrl, { ...auth, action: 'add', email: address });
    if (say(result, 'Shared.')) {
      setEmail('');
      await load();
    }
    setBusy(false);
  };

  const remove = async (person) => {
    setBusy(true);
    const result = await postJson(shareUrl, { ...auth, action: 'remove', user_id: person.id });
    if (say(result, 'Removed.')) await load();
    setBusy(false);
  };

  // The way back from a mistyped address. Until the link is opened there is
  // nobody to remove, so this is the only thing standing between a slip of the
  // thumb and a stranger with a key to the living room.
  const revokeInvite = async (invite) => {
    setBusy(true);
    const result = await postJson(shareUrl, { ...auth, action: 'revoke-invite', invite_id: invite.id });
    if (say(result, 'Invitation withdrawn.')) await load();
    setBusy(false);
  };

  const transfer = async (person) => {
    setBusy(true);
    const url = encodeURI(import.meta.env.REACT_APP_API_SERVER_URL + '/device/transfer/');
    const result = await postJson(url, { ...auth, user_id: person.id });
    if (say(result, 'Transferred.')) {
      setHandingTo(null);
      // The list this app holds now names the wrong owner, and half the controls
      // on screen are no longer this account's to use. The parent reloads rather
      // than this panel patching itself.
      if (onTransferred) await onTransferred();
    }
    setBusy(false);
  };

  if (!state) {
    return <div className="centered"><span className="spinner" /></div>;
  }

  if (state.unavailable) {
    return (
      <div className="section">
        <div className="section__head"><h2>Shared with</h2></div>
        <p className="hint">
          {state.reason || 'Sharing cannot be reached at the moment.'}
        </p>
      </div>
    );
  }

  const members = state.members || [];
  const invitations = state.invitations || [];
  // An invitation is a seat already promised, so it counts against the cap the
  // same as a member does. Counting only members would let ten pending ones sit
  // alongside ten people and make the cap mean nothing.
  const full = members.length + invitations.length >= (state.max || 10);

  return (
    <div className="section">
      <div className="section__head">
        <h2>Shared with</h2>
        <span>{members.length ? `${members.length} of ${state.max || 10}` : 'Nobody yet'}</span>
      </div>

      {error && <div className="notice notice--warn">{error}</div>}
      {notice && <div className="notice notice--info">{notice}</div>}

      {state.owner && (
        <div className="pick">
          <span className="now-playing__meta">
            {state.owner.name || state.owner.email}
            <small style={{ display: 'block', color: 'var(--muted)' }}>Owner</small>
          </span>
        </div>
      )}

      {members.map((person) => {
        const isSelf = String(person.id) === String(User.id);
        return (
          <div className="pick" key={person.id}>
            <span className="now-playing__meta">
              {person.name || person.email}
              {person.name && <small style={{ display: 'block', color: 'var(--muted)' }}>{person.email}</small>}
            </span>
            {(
              <button
                className="btn btn--sm btn--warn"
                disabled={busy}
                onClick={() => remove(person)}
                aria-label={isSelf ? 'Remove my own access' : `Remove ${person.name || person.email}`}
              >
                {isSelf ? 'Leave' : 'Remove'}
              </button>
            )}
          </div>
        );
      })}

      {/* Asked, but not arrived. Invisible until now, which is how somebody
          shares a box three times over wondering why nothing happened -- and
          why the owner had no way to take back an address they mistyped. */}
      {invitations.map((invite) => (
        <div className="pick" key={`invite-${invite.id}`}>
          <span className="now-playing__meta">
            {invite.email}
            <small style={{ display: 'block', color: 'var(--muted)' }}>
              {Number(invite.expired) === 1
                ? 'Invitation expired · not accepted'
                : 'Invited · waiting for them to accept'}
            </small>
          </span>
          <button
            className="btn btn--sm btn--warn"
            disabled={busy}
            onClick={() => revokeInvite(invite)}
            aria-label={`Withdraw the invitation to ${invite.email}`}
          >
            Withdraw
          </button>
        </div>
      ))}

      <form onSubmit={share}>
        <div className="field">
          <span className="field__label">Share with an email address</span>
          <input
            type="email"
            value={email}
            placeholder="name@example.com"
            disabled={busy || full}
            onChange={(e) => setEmail(e.target.value)} />
        </div>
        <button className="btn btn--primary btn--block" type="submit" disabled={busy || full || !email.trim()}>
          {busy ? <span className="spinner" /> : <><Icon name="plus" size={16} /> Share this box</>}
        </button>
      </form>

      <p className="hint">
        If they already have a Prayer Box account it appears in their app
        straight away. If they do not, we email them an invitation and the
        account is created when they open it &mdash; there is nothing they need
        to do first. They will be able to change the pictures and the sounds,
        control the recitation and see how the box is doing &mdash; but not move
        it, change how prayer times are worked out, rename it, or restart it.
      </p>

      {members.length > 0 && (
        <div className="panel">
          <div className="panel__head"><strong>Hand this box to someone else</strong></div>
          <p className="hint" style={{ border: 0, padding: '0 0 .5rem' }}>
            They become the owner and you become a member, keeping the access you
            have now. Only they can undo it.
          </p>

          {members.map((person) => (
            <div className="cmd" key={`hand-${person.id}`}>
              <span className="cmd__name">{person.name || person.email}</span>
              {handingTo === person.id ? (
                <span className="btn-row" style={{ margin: 0 }}>
                  <button className="btn btn--sm btn--warn" disabled={busy} onClick={() => transfer(person)}>
                    Yes, hand it over
                  </button>
                  <button className="btn btn--sm" disabled={busy} onClick={() => setHandingTo(null)}>
                    Cancel
                  </button>
                </span>
              ) : (
                <button
                  className="btn btn--sm"
                  style={{ marginLeft: 'auto' }}
                  disabled={busy}
                  onClick={() => { setHandingTo(person.id); setError(''); setNotice(''); }}
                >
                  Transfer
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

DeviceShare.propTypes = {
  User:          PropTypes.object.isRequired,
  device:        PropTypes.object,
  onTransferred: PropTypes.func,
};

export default DeviceShare;
